/**
 * Iter 67eg — Per-vendor REST API key manager.
 *
 * Vendors mint / view / revoke API keys for programmatic access to the v1
 * REST API (cars, bookings, availability blocks). The plaintext key is
 * shown **once** on creation (GitHub PAT pattern) — afterwards we only
 * display the 14-char `prefix` so they can still identify which key is
 * which without ever leaking secret material to a screenshare.
 *
 * Sits in the vendor dashboard next to the iCal channel-sync workflow.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  KeyRound,
  Copy,
  Check,
  Trash2,
  Plus,
  ShieldAlert,
  ExternalLink,
} from "lucide-react";

function _formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function VendorApiKeysPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [justCreated, setJustCreated] = useState(null);  // { id, key, label, prefix }
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const refresh = async () => {
    try {
      const { data: payload } = await api.get("/vendor/api-keys");
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load API keys.");
    }
  };

  useEffect(() => { refresh(); }, []);

  const create = async () => {
    setError(null);
    if (!label.trim()) {
      setError("Pick a label so you can identify this key later.");
      return;
    }
    setBusy(true);
    try {
      const { data: created } = await api.post("/vendor/api-keys", { label: label.trim() });
      setJustCreated(created);
      setLabel("");
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not mint key.");
    } finally { setBusy(false); }
  };

  const revoke = async (keyId, prefix) => {
    if (!window.confirm(`Revoke key ${prefix}? Any partner using it will start receiving 401s immediately.`)) return;
    setBusy(true);
    try {
      await api.delete(`/vendor/api-keys/${keyId}`);
      await refresh();
    } finally { setBusy(false); }
  };

  const copyKey = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard denied */ }
  };

  if (!data) {
    return (
      <div className="rounded-2xl border border-border p-4 bg-neutral-50/40" data-testid="vendor-api-keys-loading">
        <div className="text-sm text-neutral-500">Loading API keys…</div>
      </div>
    );
  }

  const keys = data.keys || [];
  const max = data.limits?.max_keys || 8;
  const active = keys.filter((k) => k.is_active);
  const atLimit = active.length >= max;

  return (
    <div
      className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-white p-5"
      data-testid="vendor-api-keys"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-indigo-100 ring-1 ring-indigo-200 flex items-center justify-center shrink-0">
          <KeyRound className="h-5 w-5 text-indigo-700" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-semibold">REST API keys</div>
          <p className="text-xs text-neutral-600 mt-1 leading-relaxed">
            Generate API keys to let channel managers or your own systems read
            your cars + bookings and push availability blocks programmatically
            via the <code className="px-1 py-0.5 bg-indigo-100 rounded">v1 REST API</code>.
            Treat keys like passwords — they're scoped to your account.
          </p>
        </div>
      </div>

      {/* Just-created key — shown once, then disappears on next refresh */}
      {justCreated && (
        <div
          className="rounded-xl border-2 border-emerald-300 bg-emerald-50/60 p-4 mb-4"
          data-testid="vendor-api-keys-just-created"
        >
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="h-4 w-4 text-emerald-700" />
            <div className="text-sm font-semibold text-emerald-900">
              Copy your new key — it won't be shown again
            </div>
          </div>
          <div className="flex items-stretch gap-2">
            <Input
              readOnly
              value={justCreated.key}
              className="font-mono text-xs flex-1 bg-white"
              onFocus={(e) => e.target.select()}
              data-testid="vendor-api-keys-just-created-key"
            />
            <Button
              onClick={() => copyKey(justCreated.key)}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap shrink-0"
              data-testid="vendor-api-keys-just-created-copy"
            >
              {copied ? <><Check className="h-4 w-4 mr-1.5" /> Copied</> : <><Copy className="h-4 w-4 mr-1.5" /> Copy</>}
            </Button>
          </div>
          <button
            type="button"
            onClick={() => setJustCreated(null)}
            className="text-[11px] text-emerald-800 underline mt-2"
            data-testid="vendor-api-keys-just-created-dismiss"
          >
            I've saved it — hide
          </button>
        </div>
      )}

      {/* Existing keys list */}
      <div className="space-y-2 mb-4" data-testid="vendor-api-keys-list">
        {keys.length === 0 && (
          <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50/40 p-4 text-center">
            <KeyRound className="h-5 w-5 mx-auto text-neutral-400 mb-1" />
            <div className="text-sm text-neutral-600">No API keys yet</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">Create your first key below.</div>
          </div>
        )}
        {keys.map((k) => (
          <div
            key={k.id}
            className={`rounded-xl border bg-white p-3 flex items-start gap-3 ${k.is_active ? "border-border" : "border-neutral-200 opacity-60"}`}
            data-testid={`vendor-api-keys-row-${k.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium text-sm truncate">{k.label || "Untitled"}</div>
                {!k.is_active && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full">
                    Revoked
                  </span>
                )}
              </div>
              <div className="text-[11px] text-neutral-500 mt-0.5 font-mono">
                {k.prefix}…
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">
                Last used {_formatDate(k.last_used_at)} · {k.request_count || 0} request{k.request_count === 1 ? "" : "s"} · Created {_formatDate(k.created_at)}
              </div>
            </div>
            {k.is_active && (
              <button
                type="button"
                onClick={() => revoke(k.id, k.prefix)}
                disabled={busy}
                className="h-8 w-8 rounded-lg hover:bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"
                aria-label="Revoke key"
                data-testid={`vendor-api-keys-revoke-${k.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Create form */}
      {!atLimit ? (
        <div className="rounded-xl border border-indigo-200 bg-white p-3" data-testid="vendor-api-keys-add">
          <Label className="text-xs uppercase tracking-wider font-bold text-indigo-800">
            Create new key
          </Label>
          <div className="flex items-stretch gap-2 mt-2">
            <Input
              placeholder="Label (e.g. Channel-manager, Internal CRM)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") create(); }}
              className="rounded-xl flex-1"
              data-testid="vendor-api-keys-label-input"
            />
            <Button
              onClick={create}
              disabled={busy}
              className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap shrink-0"
              data-testid="vendor-api-keys-create"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Generate
            </Button>
          </div>
          <p className="text-[11px] text-neutral-500 mt-2">
            {active.length}/{max} active keys. Send with <code className="px-1 bg-neutral-100 rounded">X-API-Key</code> header
            or <code className="px-1 bg-neutral-100 rounded">Authorization: Bearer rk_live_…</code> to any{" "}
            <span className="font-semibold">/api/v1/*</span> endpoint.
          </p>
          {error && (
            <div className="text-[11px] text-rose-600 mt-2" data-testid="vendor-api-keys-error">{error}</div>
          )}
        </div>
      ) : (
        <div className="text-[11px] text-neutral-500 italic">
          Max {max} active keys reached — revoke one to mint a new one.
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-neutral-500">
        <a
          href="https://docs.rentalcar.al/api/v1"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 hover:text-indigo-700 hover:underline"
          data-testid="vendor-api-keys-docs-link"
        >
          API docs <ExternalLink className="h-3 w-3" />
        </a>
        <span>
          Available: <code className="px-1 bg-neutral-100 rounded">GET /api/v1/me</code>,{" "}
          <code className="px-1 bg-neutral-100 rounded">/cars</code>,{" "}
          <code className="px-1 bg-neutral-100 rounded">/bookings</code>,{" "}
          <code className="px-1 bg-neutral-100 rounded">POST /cars/:id/availability-blocks</code>.
        </span>
      </div>
    </div>
  );
}
